/**
 * ============================================================================
 * TWO-FACTOR AUTHENTICATION SERVICE (TOTP + EMAIL RECOVERY)
 * ============================================================================
 * 
 * Provides optional TOTP-based 2FA using Google Authenticator, Authy, etc.
 * 
 * Features:
 * - Generate QR code for authenticator app setup
 * - Verify TOTP codes
 * - Enable/disable 2FA for user account
 * - Backup codes for account recovery
 * - EMAIL RECOVERY: Lost authenticator? Get code via email
 * 
 * Location: web-app/src/services/twoFactorService.ts
 * ============================================================================
 */

import { auth, db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import { API_BASE } from '../utils/apiBase';

// ============================================================================
// TYPES
// ============================================================================

export interface TwoFactorStatus {
  enabled: boolean;
  enabledAt?: Date;
  backupCodesRemaining?: number;
  email?: string;
  emailRecoveryAvailable?: boolean;
}

export interface TwoFactorSetupData {
  secret: string;
  qrCodeDataUrl: string;
  manualEntryKey: string;
}

export interface VerifyResult {
  success: boolean;
  error?: string;
  backupCodes?: string[];
  remainingAttempts?: number;
  lockedUntil?: Date;
}

export interface LoginRateLimitStatus {
  locked: boolean;
  lockedUntil?: Date;
  remainingAttempts?: number;
  message?: string;
}

export interface EmailRecoveryResult {
  success: boolean;
  message?: string;
  error?: string;
  expiresInMinutes?: number;
  remainingAttempts?: number;
  userId?: string;
}

// ============================================================================
// API BASE URL
// ============================================================================


// ============================================================================
// HELPER: Get Auth Token
// ============================================================================

async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }
  return user.getIdToken();
}

// ============================================================================
// 2FA STATUS
// ============================================================================

/**
 * Get 2FA status for current user
 */
export async function get2FAStatus(): Promise<TwoFactorStatus> {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${API_BASE}/2fa/status`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get 2FA status');
    }

    const data = await response.json();
    return {
      enabled: data.enabled,
      enabledAt: data.enabledAt ? new Date(data.enabledAt) : undefined,
      backupCodesRemaining: data.backupCodesRemaining,
      email: data.email,
      emailRecoveryAvailable: data.emailRecoveryAvailable,
    };
  } catch (error: any) {
    console.error('❌ [2FA] Error getting status:', error);
    
    // Fallback to direct Firestore check
    const user = auth.currentUser;
    if (user) {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        return {
          enabled: data.twoFactorEnabled === true,
          backupCodesRemaining: data.backupCodesRemaining || 0,
          email: data.email,
          emailRecoveryAvailable: !!data.email,
        };
      }
    }
    
    return { enabled: false };
  }
}

/**
 * Check if 2FA is required for login (pre-authentication)
 */
export async function check2FARequired(email: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/2fa/check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    return data.twoFactorRequired === true;
  } catch (error) {
    console.error('❌ [2FA] Error checking requirement:', error);
    return false;
  }
}

// ============================================================================
// 2FA SETUP
// ============================================================================

/**
 * Start 2FA setup - generates secret and QR code
 */
export async function setup2FA(): Promise<TwoFactorSetupData> {
  const token = await getAuthToken();
  
  const response = await fetch(`${API_BASE}/2fa/setup`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to setup 2FA');
  }

  return response.json();
}

/**
 * Verify TOTP code and enable 2FA
 * Returns backup codes on success
 */
export async function verify2FASetup(code: string): Promise<VerifyResult> {
  const token = await getAuthToken();
  
  const response = await fetch(`${API_BASE}/2fa/verify-setup`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code }),
  });

  const data = await response.json();

  if (!response.ok) {
    return {
      success: false,
      error: data.error || 'Verification failed',
    };
  }

  return {
    success: true,
    backupCodes: data.backupCodes,
  };
}

/**
 * Disable 2FA for current user
 */
export async function disable2FA(code: string): Promise<VerifyResult> {
  const token = await getAuthToken();
  
  const response = await fetch(`${API_BASE}/2fa/disable`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code }),
  });

  const data = await response.json();

  if (!response.ok) {
    return {
      success: false,
      error: data.error || 'Failed to disable 2FA',
    };
  }

  return { success: true };
}

/**
 * Regenerate backup codes
 */
export async function regenerateBackupCodes(code: string): Promise<VerifyResult> {
  const token = await getAuthToken();
  
  const response = await fetch(`${API_BASE}/2fa/regenerate-backup`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code }),
  });

  const data = await response.json();

  if (!response.ok) {
    return {
      success: false,
      error: data.error || 'Failed to regenerate codes',
    };
  }

  return {
    success: true,
    backupCodes: data.backupCodes,
  };
}

// ============================================================================
// 2FA LOGIN VERIFICATION
// ============================================================================

/**
 * Verify 2FA code during login
 */
export async function verifyLoginCode(
  email: string, 
  code: string,
  tempToken?: string
): Promise<VerifyResult> {
  const response = await fetch(`${API_BASE}/2fa/verify-login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code, tempToken }),
  });

  const data = await response.json();

  if (!response.ok) {
    return {
      success: false,
      error: data.error || 'Verification failed',
    };
  }

  return { 
    success: true,
    // Inform user if backup code was used
    ...(data.backupCodeUsed && { error: 'Backup code used. Consider generating new backup codes.' }),
  };
}

// ============================================================================
//  EMAIL RECOVERY (Lost Authenticator)

// ============================================================================

/**
 * Request email recovery code when authenticator is lost
 * Sends a temporary code to the user's registered email
 */
export async function requestEmailRecovery(email: string): Promise<EmailRecoveryResult> {
  try {
    const response = await fetch(`${API_BASE}/2fa/request-email-recovery`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Failed to send recovery email',
      };
    }

    return {
      success: true,
      message: data.message,
      expiresInMinutes: data.expiresInMinutes || 15,
    };
  } catch (error: any) {
    console.error('❌ [2FA] Email recovery request error:', error);
    return {
      success: false,
      error: 'Network error. Please try again.',
    };
  }
}

/**
 * Verify email recovery code
 * Allows user to log in without authenticator
 */
export async function verifyEmailRecovery(
  email: string, 
  code: string
): Promise<EmailRecoveryResult> {
  try {
    const response = await fetch(`${API_BASE}/2fa/verify-email-recovery`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Invalid recovery code',
        remainingAttempts: data.remainingAttempts,
      };
    }

    return {
      success: true,
      message: data.message,
      userId: data.userId,
    };
  } catch (error: any) {
    console.error('❌ [2FA] Email recovery verify error:', error);
    return {
      success: false,
      error: 'Network error. Please try again.',
    };
  }
}

// ============================================================================
// RATE LIMITING
// ============================================================================

/**
 * Check login rate limit status
 */
export async function checkLoginRateLimit(email: string): Promise<LoginRateLimitStatus> {
  try {
    const response = await fetch(`${API_BASE}/auth/rate-limit-status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    return {
      locked: data.locked,
      lockedUntil: data.lockedUntil ? new Date(data.lockedUntil) : undefined,
      remainingAttempts: data.remainingAttempts,
      message: data.message,
    };
  } catch (error) {
    console.error('❌ [RATE_LIMIT] Error checking status:', error);
    return { locked: false };
  }
}

/**
 * Record a failed login attempt
 */
export async function recordFailedLogin(email: string): Promise<LoginRateLimitStatus> {
  try {
    const response = await fetch(`${API_BASE}/auth/record-failed-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    return {
      locked: data.locked,
      lockedUntil: data.lockedUntil ? new Date(data.lockedUntil) : undefined,
      remainingAttempts: data.remainingAttempts,
      message: data.message,
    };
  } catch (error) {
    console.error('❌ [RATE_LIMIT] Error recording failed login:', error);
    return { locked: false };
  }
}

/**
 * Clear rate limit on successful login
 */
export async function clearLoginRateLimit(email: string): Promise<void> {
  try {
    await fetch(`${API_BASE}/auth/clear-rate-limit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
  } catch (error) {
    console.error('❌ [RATE_LIMIT] Error clearing rate limit:', error);
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export const twoFactorService = {
  // Status
  get2FAStatus,
  check2FARequired,
  
  // Setup
  setup2FA,
  verify2FASetup,
  disable2FA,
  regenerateBackupCodes,
  
  // Login verification
  verifyLoginCode,
  
  // Email recovery (NEW)
  requestEmailRecovery,
  verifyEmailRecovery,
  
  // Rate limiting
  checkLoginRateLimit,
  recordFailedLogin,
  clearLoginRateLimit,
};

export default twoFactorService;